// assets/AllCourses/Exercises/PictureFillInTheBlanksExercise.jsx

import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { toast } from "sonner";
import { useUploadCourseFileMutation } from "@/features/api/courseApi";
import useExerciseEngine from "@/hooks/useExerciseEngine";

/* =========================================================
   COMPONENT
========================================================= */

export default function PictureFillInTheBlanksExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";
  const [uploadFile] = useUploadCourseFileMutation();

  /* ================= SAFE QUESTIONS ================= */
  const rawQuestions = Array.isArray(
    exercise.pictureFillInTheBlanksQuestions
  )
    ? exercise.pictureFillInTheBlanksQuestions
    : [];

  /* ================= ENGINE QUESTIONS ================= */
  const questions = rawQuestions.map((q) => ({
    ...q,
    type: "fill", // 🔥 IMPORTANT for engine
  }));

  /* ================= ENGINE ================= */
  const {
    answers,
    setAnswer,
    submitIfReady,
    getInputState,
    reset,
  } = useExerciseEngine({
    questions,
    showResult: showResults,
    onComplete,
    externalAnswers: userAnswer || {},
  });

  

  /* ================= MEDIA ================= */
  const resolveMediaUrl = (url) => {
    if (!url) return "";
    const file = url.split("/").pop();
    return file ? `/uploads/image/${file}` : "";
  };

  /* ================= EDIT HELPERS ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("pictureFillInTheBlanksQuestions", next);
  };

  const updateQuestion = (qIdx, patch) => {
    const next = [...rawQuestions];
    next[qIdx] = { ...next[qIdx], ...patch };
    updateQuestions(next);
  };

  const uploadImage = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await uploadFile(fd).unwrap();
      return res.fileUrl || res.url || "";
    } catch {
      toast.error("Image upload failed");
      return "";
    }
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {rawQuestions.map((q, qIdx) => {
          const blanks = (q.sentence?.match(/_+/g) || []).length;

          return (
            <Card key={qIdx} className="p-6 space-y-6">
              <TextareaField
                label={`Sentence ${qIdx + 1} (use ___ for blanks)`}
                value={q.sentence || ""}
                onChange={(v) => {
                  const count = (v.match(/_+/g) || []).length;
                  updateQuestion(qIdx, {
                    sentence: v,
                    answers: Array.from(
                      { length: count },
                      (_, i) => q.answers?.[i] || ""
                    ),
                  });
                }}
                rows={4}
                required
              />

              {/* IMAGE */}
              <div className="space-y-3">
                <label className="font-medium">Image</label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const url = await uploadImage(file);
                    if (url) updateQuestion(qIdx, { image: url });
                  }}
                />

                {q.image && (
                  <img
                    src={resolveMediaUrl(q.image)}
                    className="max-w-xs rounded-lg border"
                  />
                )}
              </div>

              {/* ANSWERS */}
              <div className="space-y-3">
                <label className="font-medium">Correct Answers</label>

                {Array.from({ length: blanks }).map((_, i) => (
                  <StringField
                    key={i}
                    label={`Blank ${i + 1}`}
                    value={q.answers?.[i] || ""}
                    onChange={(v) => {
                      const next = [...(q.answers || [])];
                      next[i] = v;
                      updateQuestion(qIdx, { answers: next });
                    }}
                  />
                ))}
              </div>

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) =>
                  updateQuestion(qIdx, { requireCapitalization: c })
                }
              />

              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  updateQuestions(
                    rawQuestions.filter((_, i) => i !== qIdx)
                  )
                }
              >
                Remove Question
              </Button>
            </Card>
          );
        })}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...rawQuestions,
              {
                sentence: "",
                answers: [],
                image: "",
                requireCapitalization: false,
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!rawQuestions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No questions available
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT MODE ================= */
  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Picture Fill in the Blanks</CardTitle>
        <CardDescription>
          Look at the image and complete the sentence
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-12">
        {rawQuestions.map((q, qIdx) => {
          const parts = q.sentence.split(/(_+)/g).filter(Boolean);
          let blankIndex = 0;

          return (
            <div key={qIdx} className="space-y-8">
              {/* IMAGE */}
              {q.image && (
                <div className="flex justify-center">
                  <img
                    src={resolveMediaUrl(q.image)}
                    className="max-h-80 rounded-xl border shadow"
                  />
                </div>
              )}

              {/* SENTENCE */}
              <div className="p-8 bg-muted/40 rounded-xl flex flex-wrap justify-center gap-3 text-lg">
                {parts.map((part, i) => {
                  if (/_+/.test(part)) {
                    const bIdx = blankIndex++;
                    const key = `${qIdx}-${bIdx}`;

                    const state = getInputState(
                      answers[key],
                      q.answers?.[bIdx]
                    );

                    return (
                      <Input
                        key={i}
                        value={answers[key] || ""}
                        onChange={(e) => {
                          setAnswer(key, e.target.value);
                          submitIfReady();
                        }}
                        disabled={showResults}
                        className={`w-36 text-center font-semibold ${state.className}`}
                      />
                    );
                  }

                  return <span key={i}>{part}</span>;
                })}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}